import React from 'react';
import { createUseStyles } from 'react-jss';
import FormControl from '@mui/material/FormControl';
import ListItemText from '@mui/material/ListItemText';
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Checkbox from '@mui/material/Checkbox';
import {
    Select as MuiMultiSelect,
    MenuItem,
    SelectProps as MuiMultiSelectProps
} from "@mui/material";
import ErrorMessage from './ErrorMessage/ErrorMessage';


const useStyles = createUseStyles((theme: any) => ({
    select: {
        "& .MuiInputBase-input": {
            fontWeight: 400,
            fontSize: "16px",
        },
        border: "1px solid",
        borderColor: theme.palette.borderV2.tertiary.tertiary100,
        borderRadius: '8px !important',
        height: "56px",
        cursor: 'pointer !important'
    },
    label: {
        color: theme.palette.textV2.tertiary.tertiary600,
        fontSize: "16px",
        fontWeight: 400,
    },
    placeholder: {
        color: `${theme.palette.textV2.tertiary.tertiary400} !important`
    },
    checkedInputColor: {
        color: theme.palette.textV2.tertiary.tertiary300,
        '&.Mui-checked': {
            color: theme.palette.textV2.primary.primary400,
        },
    },
    primaryHighlighter: {
        background: theme.palette.backgroundV2.primary.primary500,
        color: theme.palette.textV2.tertiary.tertiary500
    }
}));

export interface IMultiSelectOption {
    label: string;
    value: string | number;
}

interface MultiSelectInputProps extends Omit<MuiMultiSelectProps, "label"> {
    options: IMultiSelectOption[];
    label: string;
    helperText?: string;
    value: IMultiSelectOption[];
    onchange: (selected: IMultiSelectOption[]) => void;
    placeholder?: string;
}

const MultiSelectInput2: React.FC<MultiSelectInputProps> = ({ options, label, helperText, value, onchange, placeholder, ...props }) => {
    const classes = useStyles();

    const handleChange = (event: any) => {
        const selectedValues = event.target.value as (string | number)[];
        const selectedOptions = options.filter(option => selectedValues.includes(option.value));
        onchange(selectedOptions);
    };

    const renderValue = (selected: unknown) => {
        const selectedValues = selected as (string | number)[];
        const selectedOptions = options.filter(option => selectedValues.includes(option.value));
        if (selectedOptions.length === 0) {
            return <span className={`${classes.placeholder}`}>{placeholder}</span>
        } else if (selectedOptions.length === 1) {
            return selectedOptions[0].label;
        } else if (selectedOptions.length === 2) {
            return selectedOptions.map(option => option.label).join(', ');
        } else {
            const firstTwo = selectedOptions.slice(0, 2).map(option => option.label).join(', ');
            return ( 
                <div>{firstTwo} <span className={`py-0.5 px-1 text-sm rounded-xl ${classes.primaryHighlighter}`}>+{selectedOptions.length - 2}</span></div>
            )
        }
    };

    return (
        <Box display="flex" flexDirection="column" gap="6px" width='100%'>
            {label && (
                <div className={classes.label}>
                    {label}
                </div>
            )}
            <MuiMultiSelect
                id={`multi-select-${label}`}
                variant='standard'
                disableUnderline={true}
                displayEmpty
                {...props}
                value={value?.map(option => option?.value)}
                multiple
                size='medium'
                className={`${classes.select} p-4`}
                renderValue={(selected) => {
                    return  (value?.length || (selected as (number | string)[])?.length) ? renderValue(selected) : <span className={`${classes.placeholder} !font-normal `}>{placeholder}</span>;
                }}
                onChange={handleChange}
            >
                {options.map((option: any) => (
                    <MenuItem key={option.value} value={option.value} >
                        <Checkbox className={classes.checkedInputColor} checked={value.some(selectedOption => selectedOption.value === option.value)} />
                        <ListItemText primary={option.label} />
                    </MenuItem>
                ))}
            </MuiMultiSelect>
            {
                props.error === true && <ErrorMessage message={helperText} />
            }
        </Box>
    )
}

export default MultiSelectInput2;
